import React from 'react';
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import {
  Calculator,
  Camera,
  LayoutGrid,
  Pause,
  RotateCcw,
  Sparkles,
  Square,
  Zap,
} from 'lucide-react';
import {brand, brandFont} from '../brand';
import {TickRing} from '../components/app/TickRing';
import {BgGradient} from '../components/BgGradient';

export type FeatureShowcaseProps = {
  appName: string;
  headline: string;
};

const PHONE_W = 600;
const PHONE_H = 1230;
const PHONE_X = (1080 - PHONE_W) / 2;
const PHONE_Y = 420;

export const FeatureShowcase: React.FC<FeatureShowcaseProps> = ({
  appName,
  headline,
}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const headerOpacity = interpolate(frame, [0, 14], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const headlineY = interpolate(frame, [0, 18], [30, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const phoneSpring = spring({
    frame: Math.max(0, frame - 10),
    fps,
    from: 0,
    to: 1,
    config: {damping: 14, stiffness: 90},
  });
  const phoneY = interpolate(phoneSpring, [0, 1], [80, 0]);
  const phoneOpacity = interpolate(frame, [10, 28], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill>
      <Audio src={staticFile('music/hook-reel.mp3')} volume={0.4} />
      <BgGradient />

      <AbsoluteFill
        style={{
          alignItems: 'center',
          justifyContent: 'flex-start',
          paddingTop: 140,
          opacity: headerOpacity,
          transform: `translateY(${headlineY}px)`,
        }}
      >
        <AppNameRow appName={appName} />
        <div style={{height: 24}} />
        <Headline text={headline} />
      </AbsoluteFill>

      <AbsoluteFill
        style={{
          opacity: phoneOpacity,
          transform: `translateY(${phoneY}px)`,
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: PHONE_X,
            top: PHONE_Y,
            width: PHONE_W,
            height: PHONE_H,
          }}
        >
          <PhoneMockup>
            <PhoneScreenTimer />
          </PhoneMockup>
        </div>
      </AbsoluteFill>

      <FloatingCard
        startFrame={25}
        x={28}
        y={520}
        rotation={-7}
        floatOffset={0}
      >
        <AIParserCard />
      </FloatingCard>

      <FloatingCard
        startFrame={32}
        x={680}
        y={620}
        rotation={6}
        floatOffset={11}
      >
        <PhotoOcrCard />
      </FloatingCard>

      <FloatingCard
        startFrame={39}
        x={40}
        y={1120}
        rotation={-5}
        floatOffset={22}
      >
        <FormatsCard />
      </FloatingCard>

      <FloatingCard
        startFrame={46}
        x={620}
        y={1310}
        rotation={7}
        floatOffset={33}
      >
        <TimeCalculatorCard />
      </FloatingCard>
    </AbsoluteFill>
  );
};

const AppNameRow: React.FC<{appName: string}> = ({appName}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 18,
      fontFamily: brandFont,
      fontSize: 40,
      fontWeight: 600,
      color: brand.textPrimary,
    }}
  >
    <Img
      src={staticFile('logo.png')}
      style={{
        width: 56,
        height: 56,
        borderRadius: 14,
        boxShadow: `0 4px 14px ${brand.primary}55`,
      }}
    />
    <span>{appName}</span>
  </div>
);

const Headline: React.FC<{text: string}> = ({text}) => (
  <div
    style={{
      fontFamily: brandFont,
      fontSize: 96,
      fontWeight: 800,
      color: brand.textPrimary,
      letterSpacing: -3,
      lineHeight: 0.98,
      textAlign: 'center',
      whiteSpace: 'pre-line',
      padding: '0 60px',
      textShadow: '0 12px 40px rgba(0,0,0,0.55)',
    }}
  >
    {text}
  </div>
);

const PhoneMockup: React.FC<{children: React.ReactNode}> = ({children}) => (
  <div
    style={{
      width: '100%',
      height: '100%',
      borderRadius: 88,
      background: '#0a0a0a',
      border: '8px solid #1c1c1c',
      boxShadow: `0 40px 100px rgba(0,0,0,0.7), 0 0 80px ${brand.primary}33, inset 0 0 0 2px rgba(255,255,255,0.04)`,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: 22,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 130,
        height: 36,
        borderRadius: 999,
        background: '#000',
        zIndex: 10,
      }}
    />
    <div
      style={{
        position: 'absolute',
        inset: 8,
        borderRadius: 80,
        overflow: 'hidden',
        background: `linear-gradient(180deg, ${brand.bg} 0%, #18102e 100%)`,
      }}
    >
      {children}
    </div>
  </div>
);

const PhoneScreenTimer: React.FC = () => {
  const frame = useCurrentFrame();
  // Tabata-style cycle: WORK (purple ring, 20s) → REST (orange ring, 10s).
  // Ring fills 0 → 1 within each phase, resets at the phase boundary.
  const WORK_START = 30;
  const WORK_END = 200;
  const REST_END = 285;
  const WORK_SECONDS = 20;
  const REST_SECONDS = 10;

  const isRest = frame >= WORK_END;
  const phaseLabel = isRest ? 'REST' : 'WORK';
  const dialProgress = isRest
    ? interpolate(frame, [WORK_END, REST_END], [0, 1], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      })
    : interpolate(frame, [WORK_START, WORK_END], [0, 1], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      });
  const phaseSec = isRest ? REST_SECONDS : WORK_SECONDS;
  const remainingSec = Math.max(0, Math.round(phaseSec * (1 - dialProgress)));
  const ringColor = isRest ? brand.timerRest : brand.timerWork;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        padding: '90px 32px 90px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 36,
      }}
    >
      <div
        style={{
          fontFamily: brandFont,
          fontSize: 26,
          fontWeight: 800,
          color: brand.textPrimary,
          letterSpacing: 5,
        }}
      >
        TABATA
      </div>

      <PhoneDial
        progress={dialProgress}
        remainingSec={remainingSec}
        phaseLabel={phaseLabel}
        ringColor={ringColor}
      />

      <div
        style={{
          display: 'flex',
          gap: 20,
          marginTop: 8,
        }}
      >
        <SmallCircle bg="rgba(255,255,255,0.06)" size={56}>
          <RotateCcw size={22} color="rgba(255,255,255,0.7)" strokeWidth={2.4} />
        </SmallCircle>
        <SmallCircle bg={brand.primary} size={68} glow>
          <Pause
            size={28}
            color={brand.textPrimary}
            strokeWidth={2.6}
            fill={brand.textPrimary}
          />
        </SmallCircle>
        <SmallCircle bg="rgba(255,255,255,0.06)" size={56}>
          <Square size={20} color={brand.timerStop} strokeWidth={2.6} fill={brand.timerStop} />
        </SmallCircle>
      </div>
    </div>
  );
};

const PhoneDial: React.FC<{
  progress: number;
  remainingSec: number;
  phaseLabel: string;
  ringColor: string;
}> = ({progress, remainingSec, phaseLabel, ringColor}) => {
  const size = 280;
  const m = Math.floor(remainingSec / 60);
  const sec = remainingSec % 60;
  const display = `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  return (
    <div style={{position: 'relative', width: size, height: size}}>
      <TickRing size={size} progress={progress} color={ringColor} strokeWidth={3} />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        <div
          style={{
            fontFamily: brandFont,
            fontSize: 14,
            color: ringColor,
            fontWeight: 800,
            letterSpacing: 3,
          }}
        >
          {phaseLabel}
        </div>
        <div
          style={{
            fontFamily: brandFont,
            fontSize: 70,
            color: brand.textPrimary,
            fontWeight: 800,
            letterSpacing: -2,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
          }}
        >
          {display}
        </div>
        <div
          style={{
            fontFamily: brandFont,
            fontSize: 18,
            color: 'rgba(255,255,255,0.6)',
            fontWeight: 600,
          }}
        >
          Round 5 of 8
        </div>
      </div>
    </div>
  );
};

const SmallCircle: React.FC<{
  size: number;
  bg: string;
  glow?: boolean;
  children: React.ReactNode;
}> = ({size, bg, glow, children}) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: glow ? `0 0 20px ${brand.primary}aa` : undefined,
    }}
  >
    {children}
  </div>
);

const FloatingCard: React.FC<{
  startFrame: number;
  x: number;
  y: number;
  rotation: number;
  floatOffset: number;
  children: React.ReactNode;
}> = ({startFrame, x, y, rotation, floatOffset, children}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const enter = spring({
    frame: Math.max(0, frame - startFrame),
    fps,
    from: 0,
    to: 1,
    config: {damping: 12, stiffness: 100},
  });
  const opacity = interpolate(frame, [startFrame, startFrame + 18], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const enterRot = interpolate(enter, [0, 1], [rotation - 18, rotation]);
  const enterScale = interpolate(enter, [0, 1], [0.65, 1]);
  const floatY = Math.sin((frame + floatOffset) / 26) * 8;

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        opacity,
        transform: `translateY(${floatY}px) rotate(${enterRot}deg) scale(${enterScale})`,
        transformOrigin: 'center',
      }}
    >
      {children}
    </div>
  );
};

const Card: React.FC<{
  width: number;
  bg: string;
  shadowColor: string;
  children: React.ReactNode;
}> = ({width, bg, shadowColor, children}) => (
  <div
    style={{
      width,
      borderRadius: 32,
      padding: 22,
      background: bg,
      boxShadow: `0 28px 60px ${shadowColor}, 0 8px 18px rgba(0,0,0,0.4)`,
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
      fontFamily: brandFont,
    }}
  >
    {children}
  </div>
);

const CardHeader: React.FC<{
  Icon: React.ComponentType<{size?: number; color?: string; strokeWidth?: number}>;
  iconColor: string;
  title: string;
  titleColor: string;
}> = ({Icon, iconColor, title, titleColor}) => (
  <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
    <Icon size={26} color={iconColor} strokeWidth={2.6} />
    <span style={{fontSize: 22, fontWeight: 800, color: titleColor}}>
      {title}
    </span>
  </div>
);

const AIParserCard: React.FC = () => (
  <Card
    width={380}
    bg={`linear-gradient(135deg, ${brand.primaryDark} 0%, ${brand.primary} 100%)`}
    shadowColor={`${brand.primary}66`}
  >
    <CardHeader
      Icon={Sparkles}
      iconColor="#FFFFFF"
      title="AI Parser"
      titleColor="#FFFFFF"
    />
    <div
      style={{
        background: 'rgba(0,0,0,0.18)',
        borderRadius: 18,
        padding: 14,
        fontSize: 18,
        lineHeight: 1.4,
        color: 'rgba(255,255,255,0.96)',
        whiteSpace: 'pre-line',
      }}
    >
      {`For time:\n4 rounds\n12 DB snatches\n10 burpees`}
    </div>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: 18,
        fontWeight: 800,
        color: '#FFFFFF',
        letterSpacing: 1,
      }}
    >
      <span>Ready to start</span>
      <Zap size={22} color="#FFFFFF" strokeWidth={2.6} fill="#FFFFFF" />
    </div>
  </Card>
);

const PhotoOcrCard: React.FC = () => (
  <Card
    width={340}
    bg={`linear-gradient(135deg, ${brand.primary} 0%, ${brand.secondary} 100%)`}
    shadowColor={`${brand.primary}66`}
  >
    <CardHeader
      Icon={Camera}
      iconColor="#FFFFFF"
      title="Snap a board"
      titleColor="#FFFFFF"
    />
    <div
      style={{
        background: 'rgba(0,0,0,0.32)',
        borderRadius: 16,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        fontFamily: brandFont,
        color: '#FFFFFF',
        border: '1.5px solid rgba(255,255,255,0.08)',
      }}
    >
      <div style={{fontSize: 18, fontWeight: 900, letterSpacing: 2}}>FRAN</div>
      <div style={{fontSize: 16, fontWeight: 700, opacity: 0.92}}>21-15-9</div>
      <div style={{fontSize: 14, fontWeight: 500, opacity: 0.78}}>
        Thrusters · Pull-ups
      </div>
    </div>
    <div
      style={{
        fontSize: 16,
        fontWeight: 700,
        color: 'rgba(255,255,255,0.94)',
      }}
    >
      Whiteboard photo → timer
    </div>
  </Card>
);

const FormatsCard: React.FC = () => (
  <Card
    width={420}
    bg={`linear-gradient(135deg, ${brand.secondary} 0%, ${brand.primaryLight} 100%)`}
    shadowColor={`${brand.secondary}66`}
  >
    <CardHeader
      Icon={LayoutGrid}
      iconColor="#FFFFFF"
      title="All formats"
      titleColor="#FFFFFF"
    />
    <div style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
      {['AMRAP', 'EMOM', 'TABATA', 'FOR TIME', 'INTERVALS'].map((t) => (
        <span
          key={t}
          style={{
            background: 'rgba(0,0,0,0.22)',
            borderRadius: 999,
            padding: '8px 14px',
            fontSize: 16,
            fontWeight: 800,
            color: '#FFFFFF',
            letterSpacing: 1.5,
          }}
        >
          {t}
        </span>
      ))}
    </div>
  </Card>
);

const TimeCalculatorCard: React.FC = () => (
  <Card
    width={380}
    bg={`linear-gradient(135deg, ${brand.primaryLight} 0%, ${brand.primary} 100%)`}
    shadowColor={`${brand.primary}66`}
  >
    <CardHeader
      Icon={Calculator}
      iconColor="#FFFFFF"
      title="Time calculator"
      titleColor="#FFFFFF"
    />
    <div
      style={{
        background: 'rgba(0,0,0,0.32)',
        borderRadius: 16,
        padding: '12px 16px 14px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        border: '1.5px solid rgba(255,255,255,0.06)',
      }}
    >
      <div
        style={{
          fontFamily: brandFont,
          fontSize: 14,
          fontWeight: 500,
          color: 'rgba(255,255,255,0.55)',
          letterSpacing: 0.4,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        5:34 − 2:13
      </div>
      <div
        style={{
          fontFamily: brandFont,
          fontSize: 44,
          fontWeight: 300,
          lineHeight: 1.05,
          letterSpacing: -0.5,
          fontVariantNumeric: 'tabular-nums',
          background: `linear-gradient(180deg, #FFFFFF 0%, ${brand.primaryLight} 55%, ${brand.primary} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        3:21
      </div>
    </div>
    <div
      style={{
        fontSize: 15,
        color: 'rgba(255,255,255,0.78)',
        fontWeight: 600,
      }}
    >
      Splits, laps, intervals.
    </div>
  </Card>
);

